/**
 * VM Table Row Component
 * Displays VM information in a table row with subdomain copy button
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { VM } from '../../types/vm';
import { VMStatusBadge } from './VMStatusBadge';
import {
  ClipboardIcon,
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  LinkIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface VMTableRowProps {
  vm: VM;
  onStart?: (vmId: string) => void;
  onStop?: (vmId: string) => void;
  onRestart?: (vmId: string) => void;
  onDelete?: (vmId: string) => void;
  onRetry?: (vmId: string) => void;
  onSyncStatus?: (vmId: string) => void;
  onCreateSnapshot?: (vmId: string) => void;
  isStarting?: boolean;
  isStopping?: boolean;
  isRestarting?: boolean;
  isDeleting?: boolean;
  isRetrying?: boolean;
  isSyncingStatus?: boolean;
}

export const VMTableRow: React.FC<VMTableRowProps> = ({
  vm,
  onStart,
  onStop,
  onRestart,
  onDelete,
  onRetry,
  onSyncStatus,
  onCreateSnapshot,
  isStarting = false,
  isStopping = false,
  isRestarting = false,
  isDeleting = false,
  isRetrying = false,
  isSyncingStatus = false,
}) => {
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        actionButtonRef.current &&
        !actionButtonRef.current.contains(event.target as Node)
      ) {
        setShowActions(false);
      }
    };

    if (showActions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showActions]);

  // Calculate menu position when opening
  const handleToggleActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!showActions && actionButtonRef.current) {
      const rect = actionButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setShowActions(!showActions);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const canStart = vm.status === 'stopped';
  const canStop = vm.status === 'running';
  const canRestart = vm.status === 'running';
  const canRetry = vm.status === 'error'; // Allow retry for error state VMs
  const canSyncStatus = vm.status === 'error'; // Allow sync status for error state VMs
  // Allow delete for stuck deleting VMs (force delete/retry)
  const canDelete = !['deleted'].includes(vm.status);
  const canForceDelete = vm.status === 'deleting' || vm.status === 'error';

  return (
    <tr className="hover:bg-gray-800 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div>
            <Link
              to={`/vms/${vm.id}`}
              className="text-sm font-medium text-white hover:text-primary-400 transition-colors cursor-pointer"
            >
              {vm.name}
            </Link>
            {vm.description && (
              <div className="text-sm text-gray-400">{vm.description}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <VMStatusBadge status={vm.status} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
        {vm.cpu_cores} cores
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
        {vm.memory_gb} GB
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
        {vm.storage_gb} GB
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {vm.public_subdomain ? (
          <div className="flex items-center gap-2">
            <code className="text-xs bg-gray-800 px-2 py-1 rounded font-mono text-gray-300">
              {vm.public_subdomain}
            </code>
            <button
              onClick={() => copyToClipboard(vm.public_subdomain!)}
              className="text-gray-500 hover:text-gray-300 transition-colors"
              title="Copy subdomain"
            >
              <ClipboardIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <span className="text-sm text-gray-500">Not available</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          {/* Quick Actions - Icon buttons for common actions */}
          {vm.status === 'running' && (
            <>
              {vm.public_subdomain && (
                <button
                  onClick={() => {
                    const event = new CustomEvent('viewConnectionInfo', { detail: vm });
                    window.dispatchEvent(event);
                  }}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="View connection info"
                >
                  <LinkIcon className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => onStop?.(vm.id)}
                disabled={isStopping}
                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Stop VM"
              >
                <StopIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => onRestart?.(vm.id)}
                disabled={isRestarting}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Restart VM"
              >
                <ArrowPathIcon className={`w-5 h-5 ${isRestarting ? 'animate-spin' : ''}`} />
              </button>
            </>
          )}
          {vm.status === 'stopped' && (
            <button
              onClick={() => onStart?.(vm.id)}
              disabled={isStarting}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Start VM"
            >
              <PlayIcon className="w-5 h-5" />
            </button>
          )}

          {/* More Actions Menu */}
          <div className="relative">
            <button
              ref={actionButtonRef}
              onClick={handleToggleActions}
              className="p-2 text-gray-500 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
              title="More actions"
            >
              <EllipsisVerticalIcon className="w-5 h-5" />
            </button>

            {/* Dropdown Menu */}
            {showActions && menuPosition && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowActions(false)}
                />
                <div
                  ref={menuRef}
                  className="fixed z-50 w-48 bg-gray-900 rounded-lg shadow-xl border border-gray-700 py-1"
                  style={{
                    top: menuPosition.top,
                    right: menuPosition.right,
                  }}
                >
                  {canStart && (
                    <button
                      onClick={() => {
                        onStart?.(vm.id);
                        setShowActions(false);
                      }}
                      disabled={isStarting}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <PlayIcon className="w-4 h-4 text-green-400" />
                      <span>{isStarting ? 'Starting...' : 'Start'}</span>
                    </button>
                  )}
                  {canStop && (
                    <button
                      onClick={() => {
                        onStop?.(vm.id);
                        setShowActions(false);
                      }}
                      disabled={isStopping}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <StopIcon className="w-4 h-4 text-orange-400" />
                      <span>{isStopping ? 'Stopping...' : 'Stop'}</span>
                    </button>
                  )}
                  {canRestart && (
                    <button
                      onClick={() => {
                        onRestart?.(vm.id);
                        setShowActions(false);
                      }}
                      disabled={isRestarting}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowPathIcon className={`w-4 h-4 text-blue-400 ${isRestarting ? 'animate-spin' : ''}`} />
                      <span>{isRestarting ? 'Restarting...' : 'Restart'}</span>
                    </button>
                  )}
                  {canSyncStatus && (
                    <button
                      onClick={() => {
                        onSyncStatus?.(vm.id);
                        setShowActions(false);
                      }}
                      disabled={isSyncingStatus}
                      className="w-full px-4 py-2.5 text-left text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Sync VM status (useful if status appears incorrect)"
                    >
                      <ArrowPathIcon className={`w-4 h-4 ${isSyncingStatus ? 'animate-spin' : ''}`} />
                      <span>{isSyncingStatus ? 'Syncing...' : 'Sync Status'}</span>
                    </button>
                  )}
                  {canRetry && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Retry VM creation for "${vm.name}"? This will attempt to create the VM again.`)) {
                          onRetry?.(vm.id);
                          setShowActions(false);
                        }
                      }}
                      disabled={isRetrying}
                      className="w-full px-4 py-2.5 text-left text-sm text-green-400 hover:bg-green-500/10 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Retry VM creation (useful if creation failed due to transient issues)"
                    >
                      <ArrowPathIcon className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
                      <span>{isRetrying ? 'Retrying...' : 'Retry Creation'}</span>
                    </button>
                  )}
                  {(vm.status === 'running' || vm.status === 'stopped') && (
                    <button
                      onClick={() => {
                        onCreateSnapshot?.(vm.id);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-3 transition-colors"
                      title="Create a snapshot of this VM"
                    >
                      <CameraIcon className="w-4 h-4 text-purple-400" />
                      <span>Create Snapshot</span>
                    </button>
                  )}
                  {vm.status === 'running' && vm.public_subdomain && (
                    <button
                      onClick={() => {
                        const event = new CustomEvent('viewConnectionInfo', { detail: vm });
                        window.dispatchEvent(event);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-3 transition-colors"
                    >
                      <LinkIcon className="w-4 h-4 text-blue-400" />
                      <span>Connection Info</span>
                    </button>
                  )}
                  {(canDelete || canForceDelete) && (
                    <>
                      <div className="border-t border-gray-700 my-1" />
                      {canForceDelete && vm.status === 'deleting' ? (
                        <button
                          onClick={() => {
                            if (window.confirm(`This VM is stuck in "deleting" state. Are you sure you want to retry deletion for "${vm.name}"?`)) {
                              onDelete?.(vm.id);
                              setShowActions(false);
                            }
                          }}
                          disabled={isDeleting}
                          className="w-full px-4 py-2.5 text-left text-sm text-orange-400 hover:bg-orange-500/10 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Retry deletion for stuck VM"
                        >
                          <TrashIcon className="w-4 h-4" />
                          <span>{isDeleting ? 'Deleting...' : 'Retry Delete'}</span>
                        </button>
                      ) : canForceDelete && vm.status === 'error' ? (
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to force delete VM "${vm.name}" in ERROR state? This action cannot be undone.`)) {
                              onDelete?.(vm.id);
                              setShowActions(false);
                            }
                          }}
                          disabled={isDeleting}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Force delete VM in ERROR state"
                        >
                          <TrashIcon className="w-4 h-4" />
                          <span>{isDeleting ? 'Deleting...' : 'Force Delete'}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete "${vm.name}"? This action cannot be undone.`)) {
                              onDelete?.(vm.id);
                              setShowActions(false);
                            }
                          }}
                          disabled={isDeleting}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                          <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

